package com.fireavert.dashboard.client.presentation

import com.fireavert.cache.client.Preferences
import com.fireavert.common.Try
import com.fireavert.dashboard.client.DashboardRepository
import com.fireavert.devices.data_models.data_model.DeviceType
import com.fireavert.events.active_events.client.ActiveEventsRepository
import com.fireavert.events.active_events.client.presentation.ActiveEventsV2Screen
import com.fireavert.events.active_events.data_models.ActiveSensorType
import com.fireavert.events.shared.data_models.models.EventLinkedActionType
import com.fireavert.events.shared.data_models.models.EventType
import com.fireavert.logging.Logger
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn

class DashboardInteractor(
    private val screen: DashboardScreen,
    private val dashboardRepository: DashboardRepository,
    private val activeEventsV2Screen: ActiveEventsV2Screen,
    private val activeEventsRepository: ActiveEventsRepository,
    private val preferences: Preferences,
    private val logger: Logger
) {
    suspend fun updateData(newStartDate: LocalDate?, newEndDate: LocalDate?, propertyId: Int?) {
        screen.startDate = newStartDate
        screen.endDate = newEndDate
        screen.propertyId = propertyId

        var endDate = newEndDate
        if (newStartDate == null) {
            return // Do nothing
        }
        if (endDate == null) {
            endDate = newStartDate
        }
        var prefPropertyId = preferences.globalProperty
        if (prefPropertyId == 0) {
            prefPropertyId = null
        }
        screen.isLoading = true
        val eventsAndActions =
            when (val maybe = dashboardRepository.getEventsAndActions(newStartDate, endDate, prefPropertyId)) {
                is Try.Error -> {
                    logger.e("Failed to get events and actions ${maybe.exception}")
                    screen.isLoading = false
                    return
                }

                is Try.Success -> {
                    maybe.value
                }
            }
        screen.smokeEvents =
            eventsAndActions.first.filter { it.type == EventType.SmokeAlarmActive && it.deviceType.isFireAvert }
        screen.waterEvents =
            eventsAndActions.first.filter { it.type == EventType.DeviceTriggered && it.deviceType == DeviceType.WaterSensor }
        screen.tamperEvents =
            eventsAndActions.first.filter { it.type == EventType.DeviceTriggered && it.deviceType == DeviceType.TamperSensor }

        screen.shutoffActions = eventsAndActions.second.filter { it.type == EventLinkedActionType.StoveShutoff }
        screen.textAlertActions = eventsAndActions.second.filter { it.type == EventLinkedActionType.TextAlert }
        screen.resetActions = eventsAndActions.second.filter { it.type == EventLinkedActionType.StoveReset }
        updateActiveEvents(newStartDate.atStartOfDayIn(TimeZone.currentSystemDefault()).epochSeconds)
        screen.isLoading = false
    }

    suspend fun updateActiveEvents(dateRange: Long) {
        val eventsWithActions = when (val maybe = activeEventsRepository.getActiveEventsWithActions(dateRange)) {
            is Try.Error -> {
                logger.e("Failed to get events with actions")
                emptyList()
            }
            is Try.Success -> maybe.value
        }

        val filteredEvents = eventsWithActions.filter { event ->
            // Filter out events that only have Offline sensor type
            !(event.activeSensors.size == 1 && event.activeSensors.contains(ActiveSensorType.Offline))
        }
        activeEventsV2Screen.eventsWithActions = filteredEvents
    }
}